/**
 * Astrologer Admin - Ratings & Reviews Management
 * Manage astrologer ratings, reviews, and feedback
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Avatar,
  Rating,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  Refresh,
  Star,
  StarBorder,
  Delete,
  Flag,
  CheckCircle,
  ThumbUp,
  ThumbDown,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import { api_urls } from '../../../utils/api-urls';
import { toast } from 'react-toastify';

const AstrologerRatings = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flagFilter, setFlagFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [stats, setStats] = useState({
    totalReviews: 0,
    avgRating: 0,
    fiveStarCount: 0,
    flaggedCount: 0,
    todayReviews: 0
  });

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [ratingFilter, statusFilter, flagFilter, dateFilter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (ratingFilter !== 'all') params.rating = ratingFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (flagFilter !== 'all') params.flagged = flagFilter === 'flagged';
      if (dateFilter !== 'all') params.dateRange = dateFilter;

      const response = await axios.get(`${api_urls}api/astrologer/admin/reviews`, { params });
      setReviews(response.data?.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Demo data
      setReviews([
        {
          _id: '1',
          reviewId: 'REV001',
          astrologer: {
            _id: 'ast1',
            astrologerName: 'Pandit Ramesh Sharma',
            photo: 'https://via.placeholder.com/50',
            avgRating: 4.8
          },
          customer: {
            _id: 'cust1',
            name: 'Amit Kumar',
            phone: '9876543210'
          },
          consultationType: 'chat',
          rating: 5,
          review: 'Excellent predictions! Pandit ji gave very accurate insights about my career. Highly recommended!',
          status: 'published',
          isFlagged: false,
          likes: 12,
          helpfulCount: 8,
          createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        },
        {
          _id: '2',
          reviewId: 'REV002',
          astrologer: {
            _id: 'ast2',
            astrologerName: 'Guru Lakshmi Devi',
            photo: 'https://via.placeholder.com/50',
            avgRating: 4.5
          },
          customer: {
            _id: 'cust2',
            name: 'Priya Singh',
            phone: '9876543211'
          },
          consultationType: 'call',
          rating: 4,
          review: 'Good consultation. Got proper guidance for my relationship issues.',
          status: 'published',
          isFlagged: false,
          likes: 5,
          helpfulCount: 3,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        },
        {
          _id: '3',
          reviewId: 'REV003',
          astrologer: {
            _id: 'ast1',
            astrologerName: 'Pandit Ramesh Sharma',
            photo: 'https://via.placeholder.com/50',
            avgRating: 4.8
          },
          customer: {
            _id: 'cust3',
            name: 'Rahul Verma',
            phone: '9876543212'
          },
          consultationType: 'video',
          rating: 2,
          review: 'Not satisfied. Predictions were very generic. Wasted my money.',
          status: 'flagged',
          isFlagged: true,
          flagReason: 'Potentially fake negative review',
          likes: 0,
          helpfulCount: 0,
          createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        },
        {
          _id: '4',
          reviewId: 'REV004',
          astrologer: {
            _id: 'ast3',
            astrologerName: 'Jyotishi Manoj Kumar',
            photo: 'https://via.placeholder.com/50',
            avgRating: 4.2
          },
          customer: {
            _id: 'cust4',
            name: 'Sneha Gupta',
            phone: '9876543213'
          },
          consultationType: 'chat',
          rating: 5,
          review: 'Amazing experience! Pandit ji is very knowledgeable. Must try!',
          status: 'pending',
          isFlagged: false,
          likes: 0,
          helpfulCount: 0,
          createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        },
        {
          _id: '5',
          reviewId: 'REV005',
          astrologer: {
            _id: 'ast2',
            astrologerName: 'Guru Lakshmi Devi',
            photo: 'https://via.placeholder.com/50',
            avgRating: 4.5
          },
          customer: {
            _id: 'cust5',
            name: 'Unknown User',
            phone: '9999999999'
          },
          consultationType: 'call',
          rating: 1,
          review: 'Worst astrologer ever! FRAUD! SCAM! Do not use this app!!!',
          status: 'flagged',
          isFlagged: true,
          flagReason: 'Suspicious review - possible spam/competitor',
          likes: 0,
          helpfulCount: 0,
          createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/astrologer/admin/reviews/stats`);
      setStats(response.data || {
        totalReviews: 2456,
        avgRating: 4.3,
        fiveStarCount: 1567,
        flaggedCount: 23,
        todayReviews: 45
      });
    } catch (error) {
      setStats({
        totalReviews: 2456,
        avgRating: 4.3,
        fiveStarCount: 1567,
        flaggedCount: 23,
        todayReviews: 45
      });
    }
  };

  const getStatusChip = (status, isFlagged) => {
    if (isFlagged) {
      return <Chip size="small" icon={<Flag />} color="warning" label="Flagged" />;
    }
    const config = {
      published: { color: 'success', label: 'Published' },
      pending: { color: 'info', label: 'Pending' },
      removed: { color: 'error', label: 'Removed' },
    };
    const cfg = config[status] || config.pending;
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#4CAF50';
    if (rating >= 3) return '#FFC107';
    if (rating >= 2) return '#FF9800';
    return '#F44336';
  };

  const handleApproveReview = async (review) => {
    try {
      await axios.post(`${api_urls}api/astrologer/admin/reviews/${review._id}/approve`);
      toast.success('Review approved and published');
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const handleFlagReview = async (review) => {
    try {
      await axios.post(`${api_urls}api/astrologer/admin/reviews/${review._id}/flag`);
      toast.success('Review flagged for moderation');
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error('Failed to flag review');
    }
  };

  const handleUnflagReview = async (review) => {
    try {
      await axios.post(`${api_urls}api/astrologer/admin/reviews/${review._id}/unflag`);
      toast.success('Review unflagged');
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error('Failed to unflag review');
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteReason.trim()) {
      toast.error('Please select deletion reason');
      return;
    }
    setActionLoading(true);
    try {
      await axios.delete(`${api_urls}api/astrologer/admin/reviews/${selectedReview._id}`, {
        data: { reason: deleteReason }
      });
      toast.success('Review deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      fetchReviews();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredReviews = reviews.filter(r =>
    r.reviewId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.astrologer?.astrologerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.review?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#9C27B0' }}>
        <Star sx={{ mr: 1, verticalAlign: 'middle', color: '#FFC107' }} />
        Ratings & Reviews Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                {stats.totalReviews.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">Total Reviews</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star sx={{ color: '#FFC107' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                  {stats.avgRating.toFixed(1)}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Avg Platform Rating</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ThumbUp sx={{ color: '#388e3c' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                  {stats.fiveStarCount.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">5-Star Reviews</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flag sx={{ color: '#d32f2f' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                  {stats.flaggedCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Flagged Reviews</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                {stats.todayReviews}
              </Typography>
              <Typography variant="body2" color="textSecondary">Today's Reviews</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Rating</InputLabel>
              <Select
                value={ratingFilter}
                label="Rating"
                onChange={(e) => setRatingFilter(e.target.value)}
              >
                <MenuItem value="all">All Ratings</MenuItem>
                <MenuItem value="5">5 Stars</MenuItem>
                <MenuItem value="4">4 Stars</MenuItem>
                <MenuItem value="3">3 Stars</MenuItem>
                <MenuItem value="2">2 Stars</MenuItem>
                <MenuItem value="1">1 Star</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="removed">Removed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Flag Status</InputLabel>
              <Select
                value={flagFilter}
                label="Flag Status"
                onChange={(e) => setFlagFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="flagged">Flagged Only</MenuItem>
                <MenuItem value="normal">Not Flagged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Date</InputLabel>
              <Select
                value={dateFilter}
                label="Date"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchReviews(); fetchStats(); }}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Reviews Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Review ID</TableCell>
              <TableCell>Astrologer</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredReviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No reviews found
                </TableCell>
              </TableRow>
            ) : (
              filteredReviews.map((review) => (
                <TableRow 
                  key={review._id} 
                  hover
                  sx={{ bgcolor: review.isFlagged ? '#fff3e0' : 'inherit' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {review.reviewId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={review.astrologer?.photo} sx={{ width: 32, height: 32 }} />
                      <Box>
                        <Typography variant="body2">{review.astrologer?.astrologerName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ★ {review.astrologer?.avgRating?.toFixed(1) || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{review.customer?.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {review.customer?.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating value={review.rating} readOnly size="small" />
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        sx={{ color: getRatingColor(review.rating) }}
                      >
                        ({review.rating})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                    <Typography variant="body2" noWrap title={review.review}>
                      {review.review?.substring(0, 60)}...
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={review.consultationType} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontSize: 10 }} 
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(review.status, review.isFlagged)}
                    {review.isFlagged && review.flagReason && (
                      <Typography variant="caption" display="block" color="warning.main">
                        {review.flagReason.substring(0, 30)}...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Full Review">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedReview(review);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {review.status === 'pending' && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveReview(review)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!review.isFlagged ? (
                        <Tooltip title="Flag Review">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleFlagReview(review)}
                          >
                            <Flag />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Unflag Review">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleUnflagReview(review)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Review">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedReview(review);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          Review Details - {selectedReview?.reviewId}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedReview && (
            <Grid container spacing={2}>
              {selectedReview.isFlagged && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <strong>Flagged:</strong> {selectedReview.flagReason}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Astrologer</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Avatar src={selectedReview.astrologer?.photo} />
                  <Box>
                    <Typography>{selectedReview.astrologer?.astrologerName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Rating: ★ {selectedReview.astrologer?.avgRating?.toFixed(1) || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Customer</Typography>
                <Typography>{selectedReview.customer?.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedReview.customer?.phone}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Rating</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={selectedReview.rating} readOnly />
                  <Typography fontWeight={600}>({selectedReview.rating}/5)</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Consultation Type</Typography>
                <Chip label={selectedReview.consultationType} size="small" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Full Review</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="body2">"{selectedReview.review}"</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Likes</Typography>
                <Typography>{selectedReview.likes || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Helpful Count</Typography>
                <Typography>{selectedReview.helpfulCount || 0}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Submitted On</Typography>
                <Typography>{new Date(selectedReview.createdAt).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedReview?.status === 'pending' && (
            <Button 
              variant="contained" 
              color="success"
              onClick={() => { handleApproveReview(selectedReview); setDetailDialogOpen(false); }}
            >
              Approve
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          Delete Review
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to delete this review?
          </Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            This action cannot be undone.
          </Alert>
          <FormControl fullWidth margin="normal">
            <InputLabel>Deletion Reason</InputLabel>
            <Select
              value={deleteReason}
              label="Deletion Reason"
              onChange={(e) => setDeleteReason(e.target.value)}
            >
              <MenuItem value="Spam or fake review">Spam or fake review</MenuItem>
              <MenuItem value="Inappropriate content">Inappropriate content</MenuItem>
              <MenuItem value="Competitor spam">Competitor spam</MenuItem>
              <MenuItem value="Abusive language">Abusive language</MenuItem>
              <MenuItem value="Duplicate review">Duplicate review</MenuItem>
              <MenuItem value="User request">User request</MenuItem>
              <MenuItem value="Other policy violation">Other policy violation</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteReason(''); }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteReview}
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={16} />}
          >
            Delete Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AstrologerRatings;
